import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export function Card({ className, children }: { className?: string; children: ReactNode }) {
  return (
    <div className={cn("bg-white rounded-2xl shadow-[0_0_2rem_0_rgba(136,152,170,0.15)]", className)}>
      {children}
    </div>
  );
}

export function CardHeader({
  title,
  subtitle,
  actions,
  className,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-3 px-6 pt-6 pb-0", className)}>
      <div>
        <h2 className="text-base font-semibold text-argon-dark">{title}</h2>
        {subtitle && <p className="text-sm text-argon-secondary mt-0.5">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function CardBody({ className, children }: { className?: string; children: ReactNode }) {
  return <div className={cn("p-6", className)}>{children}</div>;
}

export function StatCard({
  title,
  value,
  change,
  changeType = "neutral",
  icon,
}: {
  title: string;
  value: string | number;
  change?: string;
  changeType?: "up" | "down" | "neutral";
  icon: ReactNode;
}) {
  return (
    <Card className="overflow-hidden">
      <CardBody className="relative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-argon-secondary mb-1">{title}</p>
            <p className="text-2xl font-bold text-argon-dark">{value}</p>
            {change && (
              <p
                className={cn(
                  "text-sm font-semibold mt-2",
                  changeType === "up" && "text-argon-success",
                  changeType === "down" && "text-argon-danger",
                  changeType === "neutral" && "text-argon-secondary",
                )}
              >
                {change}
              </p>
            )}
          </div>
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-argon-primary/10 text-argon-primary">
            {icon}
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

export function DataTable({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("overflow-x-auto", className)}>
      <table className="w-full text-sm align-middle">{children}</table>
    </div>
  );
}

export function DataTableHead({ children }: { children: ReactNode }) {
  return (
    <thead>
      <tr className="border-b border-gray-100 text-left text-xs font-bold uppercase tracking-wide text-argon-secondary">
        {children}
      </tr>
    </thead>
  );
}

export function DataTableTh({ children, className }: { children: ReactNode; className?: string }) {
  return <th className={cn("px-4 py-3 font-semibold", className)}>{children}</th>;
}

export function DataTableTd({ children, className }: { children: ReactNode; className?: string }) {
  return <td className={cn("px-4 py-3 text-argon-dark", className)}>{children}</td>;
}
